import { createContext, useContext, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { resolveBrandColor } from './brand-color';
import { EMPTY_PUBLIC_BRANDING, normalizePublicBranding, type PublicBranding } from './public-branding';
import styles from './shell.module.css';

interface PublicShellContextValue {
  branding: PublicBranding;
  /** The `.public-shell` element. Popovers and overlays portal here to keep the shell's tokens. */
  container: HTMLElement | null;
}

const PublicShellContext = createContext<PublicShellContextValue>({ branding: EMPTY_PUBLIC_BRANDING, container: null });

export function usePublicShell(): PublicShellContextValue {
  return useContext(PublicShellContext);
}

/** CSS variables for the brand in both tones; the stylesheet picks one per theme. */
export function brandVariables(primaryColor: string | null): CSSProperties {
  const light = resolveBrandColor(primaryColor, 'light');
  const dark = resolveBrandColor(primaryColor, 'dark');
  return {
    '--brand-light': light.brand,
    '--on-brand-light': light.onBrand,
    '--brand-dark': dark.brand,
    '--on-brand-dark': dark.onBrand,
  } as CSSProperties;
}

/**
 * Scopes the business's theme to one container, never to `<html>`: the dashboard owns `<html>`
 * (the visitor's own theme) and the Brand settings preview renders this inside the dashboard.
 */
export function PublicBrandProvider({
  branding,
  children,
  className,
  style,
}: {
  branding: PublicBranding;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const vars = useMemo(() => brandVariables(branding.primaryColor), [branding.primaryColor]);
  const value = useMemo(() => ({ branding, container }), [branding, container]);

  return (
    <PublicShellContext.Provider value={value}>
      <div
        ref={setContainer}
        className={`public-shell ${styles.shell} ${className ?? ''}`}
        data-theme={branding.themeMode}
        style={{ ...vars, ...style }}
      >
        {children}
      </div>
    </PublicShellContext.Provider>
  );
}
